<template>
  <div class="ai-visibility-detail-topbar__selector-wrap">
    <v-autocomplete
      ref="trackerAutocomplete"
      class="ai-visibility-detail-topbar__selector"
      :items="normalizedTrackerOptions"
      :value="tracker.id"
      :item-text="trackerLabel"
      item-value="id"
      append-icon="keyboard_arrow_down"
      color="mainColor"
      dense
      outlined
      hide-details
      :menu-props="trackerMenuProps"
      @change="selectTrackerById"
    >
      <template #selection="{ item }">
        <div class="ai-visibility-detail-topbar__tracker-option">
          <status-dot :status="item.statusTone || item.status || 'active'" :size="9"/>
          <span>
            {{ trackerLabel(item) }}
            <span class="ai-visibility-detail-topbar__tracker-option-id">• Report ID: {{item.id}}</span>
          </span>
        </div>
      </template>
      <template #item="{ item }">
        <tracker-selector-item
          :tracker="item"
          :label="trackerLabel(item)"
          :renaming="renamingTrackerId === item.id"
          :transitioning="isTransitioningTracker(item)"
          @select="selectTracker"
          @rename="renameTracker"
          @change-frequency="openFrequencyDialog"
          @pause="openPauseDialog"
          @resume="resumeTracker"
          @delete="openDeleteDialog"
        />
      </template>
      <template #append-item>
        <div class="ai-visibility-tracker-selector-footer">
          <button
            type="button"
            class="ai-visibility-tracker-selector-footer__button"
            @mousedown.prevent
            @click.stop="createTracker"
          >
            <span class="material-icons-outlined">add</span>
            <span>Create a new report</span>
          </button>
        </div>
      </template>
    </v-autocomplete>

    <delete-tracker-dialog
      :open="deleteDialogOpen"
      :tracker="deleteTracker"
      :tracker-label="deleteTrackerLabel"
      :deleting="deletingTracker"
      @close="closeDeleteDialog"
      @confirm="confirmDeleteTracker"
    />

    <change-tracker-frequency-dialog
      :open="frequencyDialogOpen"
      :tracker="frequencyTracker"
      :saving="updatingFrequency"
      @close="closeFrequencyDialog"
      @confirm="confirmFrequencyUpdate"
    />

    <pause-tracker-dialog
      :open="pauseDialogOpen"
      :tracker="pauseTracker"
      :saving="pausingTracker"
      @close="closePauseDialog"
      @confirm="confirmPauseTracker"
    />
  </div>
</template>

<script>
import {
  deleteAiVisibilityTracker,
  pauseAiVisibilityTracker,
  resumeAiVisibilityTracker,
  updateAiVisibilityTracker,
  updateAiVisibilityTrackerRefreshCadence,
} from '../../api'
import ChangeTrackerFrequencyDialog from './ChangeTrackerFrequencyDialog.vue'
import DeleteTrackerDialog from './DeleteTrackerDialog.vue'
import PauseTrackerDialog from './PauseTrackerDialog.vue'
import TrackerSelectorItem from './TrackerSelectorItem.vue'
import StatusDot from '../ui/display/StatusDot.vue'

export default {
  name: 'DetailTrackerSelector',
  components: {
    ChangeTrackerFrequencyDialog,
    DeleteTrackerDialog,
    PauseTrackerDialog,
    StatusDot,
    TrackerSelectorItem,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    tracker: {
      type: Object,
      required: true,
    },
    trackerOptions: {
      type: Array,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      trackerMenuProps: {
        offsetY: true,
        contentClass: 'ai-visibility-tracker-selector-menu-content',
      },
      renamingTrackerId: null,
      deleteDialogOpen: false,
      deleteTracker: null,
      deletingTracker: false,
      frequencyDialogOpen: false,
      frequencyTracker: null,
      updatingFrequency: false,
      pauseDialogOpen: false,
      pauseTracker: null,
      pausingTracker: false,
      trackerStatusTransitionId: null,
    }
  },
  computed: {
    normalizedTrackerOptions() {
      const trackerOptions = Array.isArray(this.trackerOptions) ? this.trackerOptions : []
      const optionsById = trackerOptions.reduce((options, option) => {
        if (option && option.id != null) {
          options[String(option.id)] = option
        }
        return options
      }, {})

      if (this.tracker && this.tracker.id != null && !optionsById[String(this.tracker.id)]) {
        return [this.tracker].concat(trackerOptions)
      }

      return trackerOptions
    },
    deleteTrackerLabel() {
      return this.trackerLabel(this.deleteTracker)
    },
  },
  methods: {
    isActiveTracker(tracker) {
      return tracker && String(tracker.id) === String(this.tracker.id)
    },
    isTransitioningTracker(tracker) {
      return tracker &&
        this.trackerStatusTransitionId !== null &&
        String(this.trackerStatusTransitionId) === String(tracker.id)
    },
    trackerLabel(tracker) {
      if (!tracker) {
        return 'AI Visibility tracker'
      }

      return tracker.title || tracker.name || tracker.domain || 'AI Visibility tracker'
    },
    selectTracker(tracker) {
      if (!tracker || this.isActiveTracker(tracker)) {
        return
      }
      this.$emit('select-tracker', tracker)
    },
    selectTrackerById(trackerId) {
      const tracker = this.normalizedTrackerOptions.find((option) => String(option.id) === String(trackerId))
      this.selectTracker(tracker)
    },
    createTracker() {
      const autocomplete = this.$refs.trackerAutocomplete
      if (autocomplete) {
        autocomplete.isMenuActive = false
        if (typeof autocomplete.blur === 'function') {
          autocomplete.blur()
        }
      }
      this.$emit('create-tracker')
    },
    async renameTracker({ tracker, title, done, cancel }) {
      if (!tracker || tracker.id == null || this.renamingTrackerId !== null) {
        if (typeof cancel === 'function') {
          cancel()
        }
        return
      }

      this.renamingTrackerId = tracker.id
      try {
        const response = await updateAiVisibilityTracker(
          this.siteId,
          tracker.id,
          { title },
        )
        const updatedTracker = response.data.tracker || {
          ...tracker,
          title,
        }
        this.$emit('tracker-updated', updatedTracker)
        if (typeof done === 'function') {
          done()
        }
        this.$notify({
          group: 'info',
          type: 'success',
          text: `Report renamed to ${this.trackerLabel(updatedTracker)}`,
        })
      } catch (error) {
        if (typeof cancel === 'function') {
          cancel()
        }
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Could not rename report.',
        })
      } finally {
        this.renamingTrackerId = null
      }
    },
    openDeleteDialog(tracker) {
      this.deleteTracker = tracker
      this.deleteDialogOpen = true
    },
    openFrequencyDialog(tracker) {
      this.frequencyTracker = tracker
      this.frequencyDialogOpen = true
    },
    openPauseDialog(tracker) {
      this.pauseTracker = tracker
      this.pauseDialogOpen = true
    },
    closePauseDialog() {
      if (this.pausingTracker) {
        return
      }
      this.pauseDialogOpen = false
      this.pauseTracker = null
    },
    closeFrequencyDialog() {
      if (this.updatingFrequency) {
        return
      }
      this.frequencyDialogOpen = false
      this.frequencyTracker = null
    },
    async confirmFrequencyUpdate({ tracker, refreshFrequency }) {
      if (!tracker || tracker.id == null || !refreshFrequency) {
        return
      }

      this.updatingFrequency = true
      try {
        const response = await updateAiVisibilityTrackerRefreshCadence(
          this.siteId,
          tracker.id,
          refreshFrequency,
        )
        const updatedTracker = response.data.tracker || {
          ...tracker,
          refreshFrequency,
        }
        this.$emit('tracker-updated', updatedTracker)
        this.frequencyDialogOpen = false
        this.frequencyTracker = null
        this.$notify({
          group: 'info',
          type: 'success',
          text: `Switched to ${refreshFrequency} refresh.`,
        })
      } catch (error) {
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Could not update refresh frequency.',
        })
      } finally {
        this.updatingFrequency = false
      }
    },
    async confirmPauseTracker(tracker) {
      const trackerToPause = tracker || this.pauseTracker
      if (
        !trackerToPause ||
        trackerToPause.id == null ||
        this.trackerStatusTransitionId !== null
      ) {
        return
      }

      this.pausingTracker = true
      this.trackerStatusTransitionId = trackerToPause.id
      try {
        const response = await pauseAiVisibilityTracker(
          this.siteId,
          trackerToPause.id,
        )
        const updatedTracker = response.data.tracker || {
          ...trackerToPause,
          status: 'inactive',
          nextRunAt: null,
        }
        this.$emit('tracker-updated', updatedTracker)
        this.$emit('refresh-trackers')
        this.pauseDialogOpen = false
        this.pauseTracker = null
        this.$notify({
          group: 'info',
          type: 'success',
          text: 'Schedule paused.',
        })
      } catch (error) {
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Could not pause schedule.',
        })
      } finally {
        this.pausingTracker = false
        this.trackerStatusTransitionId = null
      }
    },
    async resumeTracker(tracker) {
      if (
        !tracker ||
        tracker.id == null ||
        this.trackerStatusTransitionId !== null
      ) {
        return
      }

      this.trackerStatusTransitionId = tracker.id
      try {
        const response = await resumeAiVisibilityTracker(
          this.siteId,
          tracker.id,
        )
        const updatedTracker = response.data.tracker || {
          ...tracker,
          status: 'active',
        }
        this.$emit('tracker-updated', updatedTracker)
        this.$emit('refresh-trackers')
        this.$notify({
          group: 'info',
          type: 'success',
          text: 'Report resumed. Your previously active report is now paused. Only one report runs at a time.',
          duration: 5000
        })
      } catch (error) {
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Could not resume schedule.',
        })
      } finally {
        this.trackerStatusTransitionId = null
      }
    },
    closeDeleteDialog() {
      if (this.deletingTracker) {
        return
      }
      this.deleteDialogOpen = false
      this.deleteTracker = null
    },
    async confirmDeleteTracker(tracker) {
      const trackerToDelete = tracker || this.deleteTracker
      if (!trackerToDelete || trackerToDelete.id == null) {
        return
      }

      this.deletingTracker = true
      try {
        await deleteAiVisibilityTracker(this.siteId, trackerToDelete.id)
        this.$notify({
          group: 'info',
          type: 'success',
          text: 'Report deleted.',
        })
        this.deleteDialogOpen = false
        this.deleteTracker = null
        this.$emit('tracker-deleted', trackerToDelete)
      } catch (error) {
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Could not delete report.',
        })
      } finally {
        this.deletingTracker = false
      }
    },
  }
}
</script>

<style scoped lang="scss">

.ai-visibility-detail-topbar__selector-wrap {
  max-width: fit-content;
}

.ai-visibility-detail-topbar__selector {
  max-width: fit-content;
}

.ai-visibility-detail-topbar__selector ::v-deep .v-input__slot {
  min-height: 42px;
  border-radius: 8px;
}

.ai-visibility-detail-topbar__selector ::v-deep fieldset {
  background-color: var(--v-cardBg-base);
  border-color: #DADADA;
}

.ai-visibility-detail-topbar__selector ::v-deep .v-select__selection {
  max-width: 100%;
}

.ai-visibility-detail-topbar__selector ::v-deep .v-input__append-inner {
  align-self: center;
  margin-top: 0;
}

.ai-visibility-detail-topbar__tracker-option {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 700;
}

.ai-visibility-detail-topbar__tracker-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-visibility-detail-topbar__tracker-option-id {
  font-size: 12px;
  font-weight: 400;
  color: var(--v-darkGrayColor-base);
}

.ai-visibility-detail-topbar__selector ::v-deep .v-list-item {
  min-height: auto;
  padding: 0 8px;
}

.ai-visibility-detail-topbar__selector ::v-deep .v-list-item__content {
  padding: 0;
}

.ai-visibility-detail-topbar__selector ::v-deep .v-list-item__title {
  width: 100%;
}


@media (max-width: 720px) {
  .ai-visibility-detail-topbar__selector-wrap,
  .ai-visibility-detail-topbar__selector {
    width: 100%;
  }
}

</style>

<style lang="scss">

.ai-visibility-tracker-selector-menu-content {
  .v-list {
    padding-bottom: 0;
  }
}

.ai-visibility-tracker-selector-footer {
  position: sticky;
  z-index: 2;
  bottom: 0;
  border-top: 1px solid var(--v-gray13Color-base);
  background: var(--v-cardBg-base);
  padding: 6px;
}

.ai-visibility-tracker-selector-footer__button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  padding: 10px 8px;
  text-align: left;

  span {
    color: var(--v-profileAnchorColor-base);
  }
}

.ai-visibility-tracker-selector-footer__button:hover,
.ai-visibility-tracker-selector-footer__button:focus-visible {
  background: var(--v-lightYellowColor-base);
  outline: none;
}

.ai-visibility-tracker-selector-footer__button .material-icons-outlined {
  font-size: 20px;
}

</style>
